// ===============================================
// File: src/contexts/PlannerContext.jsx
// Unified persistence (Folder OR Drive) via StorageContext
// Saves to: planner/events.json
// Rehydrates on boot + after restore (vault/files)
// ===============================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStorage } from "./StorageContext";

const PlannerContext = createContext(null);
export function usePlanner() {
  const ctx = useContext(PlannerContext);
  if (!ctx) throw new Error("usePlanner must be used within <PlannerProvider>");
  return ctx;
}

/* ---------------- colors/helpers ---------------- */
const STAGE_COLORS = {
  forming: "#6aa3a8",
  drying: "#a8705a",
  bisque: "#c9955a",
  glazing: "#5a86c9",
  glazeFiring: "#e66d6d",
  qcPack: "#6bbf7a",
  dispatch: "#9aa0a6",
};
const CATEGORY_COLORS = {
  production: "#4f8ef7",
  orders: "#f78c1f",
  admin: "#c28bf2",
  workshop: "#48cfae",
  marketing: "#f25f5c",
};
function hashColor(str) {
  const palette = [
    "#8ab4f8","#f28b82","#fdd663","#81c995","#a7a7f7",
    "#f4b400","#78d9ec","#cfa5e9","#ffab91","#90caf9",
  ];
  let h = 0; const s = String(str || "");
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}
function colorByStage(k){ return STAGE_COLORS[k] || "#888"; }
function colorByCategory(k){ return CATEGORY_COLORS[k] || "#9aa0a6"; }
function colorForEvent(ev){
  if (ev?.assigneeId) return hashColor(String(ev.assigneeId));
  const stage = (ev.tags || []).find((t) => STAGE_COLORS[t]);
  if (stage) return colorByStage(stage);
  if (ev?.category) return colorByCategory(ev.category);
  return "#888";
}
function toISO(d){
  if (!d) return undefined;
  const dd = d instanceof Date ? d : new Date(d);
  return new Date(dd.getFullYear(), dd.getMonth(), dd.getDate(), dd.getHours(), dd.getMinutes(), 0, 0).toISOString();
}
function normalizeEvent(ev){
  const out = { ...ev };
  if (typeof out.notes !== "string") out.notes = "";
  if (out.start) out.start = toISO(out.start);
  if (out.end) out.end = toISO(out.end);
  out.color = out.color || colorForEvent(out);
  return out;
}

const FILE_PATH = "planner/events.json";

/* ---------------- provider ---------------- */
export function PlannerProvider({ children }) {
  const { loadJSON, saveJSON, hasWorkspace } = useStorage();

  const [events, setEvents] = useState([]);
  const loadedRef = useRef(false);
  const saveDebounce = useRef(0);

  // LOAD (boot) + listen for restore
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    async function hydrate() {
      // Only load if workspace is connected
      if (!hasWorkspace) {
        if (alive) setEvents([]);
        loadedRef.current = true;
        return;
      }
      
      try {
        const initial = await loadJSON(FILE_PATH, []);
        const normalized = (Array.isArray(initial) ? initial : []).map(normalizeEvent);
        if (alive) setEvents(normalized);
      } catch {
        if (alive) setEvents([]);
      } finally {
        loadedRef.current = true;
      }
    }
    hydrate();

    const onRestored = async () => {
      if (!hasWorkspace) return;
      try {
        const r = await loadJSON(FILE_PATH, []);
        if (alive) setEvents((Array.isArray(r) ? r : []).map(normalizeEvent));
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      if (alive) {
        setEvents([]);
        loadedRef.current = false;
      }
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      alive = false;
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [loadJSON, hasWorkspace]);

  // SAVE on change (debounced)
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { await saveJSON(FILE_PATH, events); } catch {}
    }, 250);
    return () => clearTimeout(saveDebounce.current);
  }, [events, saveJSON, hasWorkspace]);

  /* ---------------- CRUD ---------------- */
  function upsertEvents(list) {
    setEvents((prev) => {
      const m = new Map(prev.map((e) => [String(e.id), e]));
      for (const e of list) {
        const id = e.id || `EVT-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const merged = normalizeEvent({ ...(m.get(String(id)) || {}), ...e, id });
        m.set(String(id), merged);
      }
      return [...m.values()].sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0));
    });
  }

  function updateEventPatch(id, patch) {
    setEvents((prev) =>
      prev.map((e) => (String(e.id) === String(id) ? normalizeEvent({ ...e, ...patch }) : e))
    );
  }

  function removeEvent(id) {
    setEvents((prev) => prev.filter((e) => String(e.id) !== String(id)));
  }
  const deleteEvent = (id) => removeEvent(id);

  function markDone(id) {
    updateEventPatch(id, { status: "done", progress: 100 });
  }

  function shiftChain(anchorId, days = 0) {
    if (!days) return;
    setEvents((prev) => {
      const byId = new Map(prev.map((e) => [String(e.id), e]));
      const visited = new Set();
      const stack = [anchorId];
      const result = [...prev];

      function shiftOne(ev) {
        const plus = (d, n) => new Date(new Date(d).getTime() + n * 86400000).toISOString();
        const patch = {};
        if (ev.start) patch.start = plus(ev.start, days);
        if (ev.end) patch.end = plus(ev.end, days);
        return { ...ev, ...patch };
      }

      while (stack.length) {
        const id = String(stack.pop());
        if (!id || visited.has(id)) continue;
        visited.add(id);
        const ev = byId.get(id);
        if (!ev) continue;
        const idx = result.findIndex((x) => String(x.id) === id);
        if (idx >= 0) result[idx] = shiftOne(result[idx]);
        prev.forEach((e) => {
          const deps = Array.isArray(e.dependencies) ? e.dependencies.map(String) : [];
          if (deps.includes(id)) stack.push(String(e.id));
        });
      }
      return result.map(normalizeEvent);
    });
  }

  function upsertFromCalculator(calc) {
    const out = [];
    const idBase = Date.now();
    (calc.plan || []).forEach(([key, s, e, qty, resource], i) => {
      const startISO = toISO(s);
      const endISO = toISO(e);
      out.push(
        normalizeEvent({
          id: `${key}-${idBase}-${i}`,
          title: key,
          start: startISO,
          end: endISO,
          status: "planned",
          tags: [key, calc.productId, calc.orderId].filter(Boolean),
          resource,
          qty,
          category: "production",
          taskType: key,
          orderId: calc.orderId,
          productId: calc.productId,
          assigneeId: undefined,
          color: colorByStage(key),
          dependencies: i === 0 ? [] : [`${key}-${idBase}-${i - 1}`],
          source: "calculator",
          notes: "",
        })
      );
    });
    upsertEvents(out);
  }

  function addTasks(list) {
    if (!Array.isArray(list) || list.length === 0) return;
    upsertEvents(list);
  }

  // One-time notes normalization
  useEffect(() => {
    setEvents((prev) => {
      let changed = false;
      const next = prev.map((e) => {
        if (typeof e.notes === "string") return e;
        changed = true;
        return { ...e, notes: "" };
      });
      return changed ? next : prev;
    });
  }, []);

  const value = useMemo(
    () => ({
      events,
      upsertEvents,
      updateEventPatch,
      removeEvent,
      deleteEvent,
      markDone,
      shiftChain,
      upsertFromCalculator,
      addTasks,
    }),
    [events]
  );

  return <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>;
}
